
import { runExtractValue } from "../../executor";
import useTemplateBuilder from "../../useTemplateBuilder";
import { canBindingNode } from "../useBinding";
import useWatcher, { usePropStore } from "../useWatcher";

import useLayerIdentity from '../../../layer/chain/validator/useLayerIdentity'
import usePropsForwarder from "../../../layer/usePropForwarder";
import useOnLayerAfterSet, { useOnAfterSetForwarder } from "../../../layer/observable/useOnLayerAfterSet";
import { useQueryLayer1 } from "../../../layer/chain/useQueryLayer";
import useChainCreator from "../../../layer/chain/useChainCreator";
import PropArray from "../../../type/propArray";
import { useSubscribeDOMDestroyed, useSubscribeDOMRendered } from "../../../observer/useDomObserver";
import { useTemplateIdentity } from "../../utils/useTemplateIdentify";
import { useMarkRootProp } from "../../../layer/chain/store/useStoreRoot";
import useMarkParentLinking from "../../../layer/utils/useMarkParentLinking";
import { setLinkingMethod } from "../../../utils/useInjectMedthod";
import { copyDeep } from "../../util";
import useWeakMap from "../../../utils/useWeakMap";
import useDiscard from "../../../useDiscard";
import { SYM_SPECIAL_DEPS } from "../../../enum/Symbol";
import useMap from "../../../utils/useMap";
import { ATTRIBUTE_FOR } from "../../enum";
import { storeLayerUsingOriginalSource } from "../../../layer/chain/store/useStoreLayerUsingOriginalSource";
import { useQueryOriginSource } from "../../../layer/chain/useQueryOriginal";
import useOnOriginalSourceChanged from "../../../layer/observable/useOnOriginalSourceChanged";
import useQueryPathByLayer from "../../../layer/chain/store/useStorePathByLayer";
import useQueryRootLayer, { useMarkRootLayer, useOverrideRootLayer } from "../../../layer/chain/store/useStoreRootLayer";
import useQueryParentLayer, { storeParentLayer } from "../../../layer/chain/store/useStoreLayerParent";
import useLogStack from "../../../layer/utils/useLogStack";

const MODE = {
    reuse: 'reuse',
    alwayNew: 'alway-new'
}
let mode = MODE.alwayNew;

const { checkNodeIsInLoopTemplate, storeNodeToLoopTemplate, deleteNode } = (() => {

    const weaksetNodeInLoop = new WeakSet()

    /** the rcz:for directive will call useTemplateBuild so this using to prevent binding from another builder instance  */
    const checkNodeIsInLoopTemplate = (node) => {
        return weaksetNodeInLoop.has(node);
    }

    const storeNodeToLoopTemplate = (node) => {
        weaksetNodeInLoop.add(node);
        node.querySelectorAll("*").forEach(n => weaksetNodeInLoop.add(n));
    }

    const deleteNode = (node) => {
        weaksetNodeInLoop.delete(node);
    }

    return {
        storeNodeToLoopTemplate,
        checkNodeIsInLoopTemplate,
        deleteNode
    }
})();


const extractCollectionAndIndicator = (props, sentence) => {
    const propL1 = useQueryLayer1(props);

    const split = sentence.split(' '),
        /* ex: let item in A - item is indicator */
        indicatorName = split[1],
        targetPropName = split[split.length - 1],
        targetCollection = runExtractValue(propL1, targetPropName);
    if (targetCollection === undefined) {
        throw new Error(`${targetPropName} undefined!`)
    }
    return [indicatorName, targetCollection];
}

const useLoopNodeCaching = (() => {
    const storeExisting = useWeakMap();

    const onDiscardProp = (props) => storeExisting.delete(props);

    const createStoreIfNotExist = (props, loopHolder) => {
        if (!storeExisting.has(props)) {
            storeExisting.set(props, useWeakMap());
        }
        if (!storeExisting.get(props).has(loopHolder)) {
            storeExisting.get(props).set(loopHolder, []);
        }
        useDiscard(props, onDiscardProp);
    }

    return (props, loopHolder) => {
        createStoreIfNotExist(props, loopHolder);
        return {
            addLoopNode: (node) => {
                storeExisting.get(props).get(loopHolder).push(node);
            },
            getExistingLoopNodes: () => {
                return storeExisting.get(props).get(loopHolder)
            },
            discardLoopStore: () => {
                if (!storeExisting.has(props)) return;
                storeExisting.get(props).delete(loopHolder)
            },
            countLoopNode: () => {
                return storeExisting.get(props).get(loopHolder).length
            },
            createStoreIfNotExist: () => {
                createStoreIfNotExist(props, loopHolder);
            }
        }
    }
})()


/**
 * 
 * @param {string} sentence 
 * @param {object} props 
 * @param {Node} loopHolder
 * @param {Node} loopNode 
 * @returns 
 */
const useLoopBuilder = (sentence, props, loopHolder, loopNode) => {
    const { addLoopNode, discardLoopStore, getExistingLoopNodes, createStoreIfNotExist } = useLoopNodeCaching(props, loopHolder)
    const cloneNode = () => loopNode.cloneNode(true);

    let originCollection = []

    const newNode = (loopItem, indicatorName) => {
        const originSource = useQueryOriginSource(loopItem);
        const { newLayer, getOriginalSource, discardChain, getRootLayer, getChainId } = useChainCreator();
        const cNode = cloneNode();


        const o = usePropsForwarder({
            [indicatorName]: originSource || loopItem,
            [SYM_SPECIAL_DEPS]: [indicatorName]
        }, props);

        
        const path = new PropArray();
        path.isSpecialPath = true;

        const [l11] = newLayer(o, path);
        const l1 = getRootLayer();

        if (originSource) {
            storeLayerUsingOriginalSource(originSource, l1[indicatorName])
        }

        // useOnOriginalSourceChanged(originSource, (...rest) => {
        //     console.log("useOnOriginalSourceChanged", rest)
        // }, '*')

        useMarkParentLinking(l1, loopItem);
        // useMarkRootProp(l1, props);
        useTemplateIdentity(props, l1);
        setLinkingMethod(props, l1);

        const parent = useQueryParentLayer(loopItem)
        if (parent) {
            storeParentLayer(l1[indicatorName], parent);
        }


        const discardForwarder = useOnAfterSetForwarder(loopItem, l1, (propPath, absKey, key, value, oldValue) => {
            const pathTranslated = propPath.clone();
            const pathInList = useQueryPathByLayer(loopItem);
            while (pathInList.length > 0) {
                const k = pathInList.shift();
                if (pathTranslated[0] === k) {
                    pathTranslated.shift();
                }
            }
            pathTranslated.unshift(indicatorName);
            return [pathTranslated, pathTranslated.pathToString(), key, value, oldValue]
        }, '*')

        useTemplateBuilder({
            rootElement: cNode,
            props: l1
        });

        storeParentLayer(l1, props)
        usePropStore().addProps(props, l1)

        const un = useSubscribeDOMDestroyed(cNode, () => {
            discardChain();
            deleteNode(cNode);
            discardForwarder();
        }, null, props);

        useDiscard(props, () => {
            discardChain();
            deleteNode(cNode);
            discardForwarder();
        })

        return { cNode, layer: l1 };
    }

    const reuseExisting = (newSource, index, indicatorName) => {
        const layer = getExistingLoopNodes()[index][0];
        layer[indicatorName] = newSource;
        console.log({ layer, newSource })
        // Object.assign(layer[indicatorName], newSource)
    }

    const render = () => {
        if (!canBindingNode(loopHolder)) {
            return;
        }
        createStoreIfNotExist();
        const [indicatorName, targetCollection] = extractCollectionAndIndicator(props, sentence);
        const fragment = document.createDocumentFragment();
        originCollection = new Array(targetCollection.length);

        if (mode === MODE.reuse) {
            const nodesCreated = getExistingLoopNodes(),
                nodeCreatedLength = nodesCreated ? nodesCreated.length : 0;
            for (let i = 0, len = targetCollection.length; i < len; i++) {
                const target = targetCollection[i];
                if (i >= nodeCreatedLength) {
                    const { cNode, layer } = newNode(target, indicatorName)
                    fragment.appendChild(cNode);
                    // nodesCreated.push([layer, cNode]);
                    addLoopNode([layer, cNode])
                } else if (i < nodeCreatedLength) {
                    reuseExisting(target, i, indicatorName);
                }
            }

            if (nodesCreated.length > targetCollection.length) {
                const removeList = nodesCreated.splice(targetCollection.length);
                while (removeList.length > 0) {
                    const [layer, node] = removeList.shift();
                    node.parentNode.removeChild(node);
                }
            }
        } else if (mode === MODE.alwayNew) {
            const children = loopHolder.children;
            while (children.length > 0) {
                children[0].parentNode.removeChild(children[0]);
            }

            for (let i = 0, len = targetCollection.length; i < len; i++) {
                const target = targetCollection[i];
                const { cNode, layer } = newNode(target, indicatorName)
                fragment.appendChild(cNode);
            }
        }
        loopHolder.appendChild(fragment);
    }

    const discard = () => {
        const children = loopHolder.children;
        while (children.length > 0) {
            children[0].parentNode.removeChild(children[0]);
        }
        discardLoopStore();
    }

    return {
        render,
        discard
    }

}

const extractPropName = (props, sentence = '') => {
    const words = sentence.split(' ');
    const last = words.pop();
    if (runExtractValue(props, last) !== undefined) {
        return last;
    } else {
        throw `rcz:for='${sentence}' ${last} does not exist in template`;
    }
}

const useFor = ((sentence, props, node, loopNode) => {


    /**
     * @param {string} sentence
     * @param {object} props
     * @param {Node} node
     */
    return (sentence, props, node) => {
        sentence = sentence.trim();
        storeNodeToLoopTemplate(node);
        const loopNode = node.cloneNode(true);
        loopNode.removeAttribute(ATTRIBUTE_FOR);
        const loopContainer = node.parentElement;
        if (loopContainer) {
            loopContainer.removeChild(node);
        }
        // const { exec, rollback } = LoopInstance();

        const { render, discard } = useLoopBuilder(sentence, props, loopContainer, loopNode);

        if (!sentence.startsWith('let') && !sentence.startsWith('var')) {
            sentence = `let ` + sentence;
        }

        const deps = extractPropName(props, sentence);

        const { onPropChanges, onDestroy, onCreated } = useWatcher(props, loopContainer, [deps])

        let waiter;
        onPropChanges(render)

        onDestroy(discard);

        return Promise.resolve();
    }
})();

export default useFor

export { checkNodeIsInLoopTemplate };